import { useState, useRef } from 'react'
import { QrCode, Download, Copy, Check, Link as LinkIcon } from 'lucide-react'
import QRCode from 'react-qr-code'

export function QRGenerator() {
  const [url, setUrl] = useState('')
  const [showQr, setShowQr] = useState(false)
  const [copied, setCopied] = useState(false)
  const qrRef = useRef<HTMLDivElement>(null)

  const handleGenerate = () => {
    if (url.trim()) {
      setShowQr(true)
    }
  }

  const handleReset = () => {
    setUrl('')
    setShowQr(false)
    setCopied(false)
  }

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Error copying URL:', err)
    }
  }

  const handleDownloadQR = (format: 'png' | 'svg') => {
    const svg = qrRef.current?.querySelector('svg')
    if (!svg) return

    if (format === 'svg') {
      const svgData = new XMLSerializer().serializeToString(svg)
      const blob = new Blob([svgData], { type: 'image/svg+xml' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'codigo_qr.svg'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } else {
      const svgData = new XMLSerializer().serializeToString(svg)
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      const img = new Image()

      img.onload = () => {
        canvas.width = 1024
        canvas.height = 1024
        ctx?.fillRect(0, 0, canvas.width, canvas.height)
        ctx!.fillStyle = 'white'
        ctx?.fillRect(0, 0, canvas.width, canvas.height)
        ctx?.drawImage(img, 0, 0, 1024, 1024)

        const pngUrl = canvas.toDataURL('image/png')
        const link = document.createElement('a')
        link.href = pngUrl
        link.download = 'codigo_qr.png'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }

      img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)))
    }
  }

  return (
    <div className="w-full max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-legal-900 dark:text-white">
          Generador de Codigos QR
        </h1>
        <p className="mt-2 text-legal-600 dark:text-legal-300">
          Genera codigos QR profesionales para incluir en documentos legales, cartas documento y traslados de demanda
        </p>
      </div>

      {!showQr ? (
        /* Input Section */
        <div className="card">
          <h2 className="text-lg font-semibold text-legal-900 dark:text-white mb-4">
            Ingresa la URL o enlace
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-legal-700 dark:text-legal-300 mb-1">
                URL del expediente, documento o recurso
              </label>
              <div className="relative">
                <LinkIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-legal-400" size={20} />
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://mev.scba.gov.ar/expedientes/..."
                  className="w-full pl-10 pr-4 py-3 border border-legal-300 dark:border-legal-600 rounded-lg bg-white dark:bg-legal-700 text-legal-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  onKeyDown={(e) => e.key === 'Enter' && handleGenerate()}
                />
              </div>
              <p className="text-xs text-legal-500 dark:text-legal-400 mt-2">
                Ejemplos: URL del expediente electronico (MEV), link a documento en la nube, página web, etc.
              </p>
            </div>

            <button
              onClick={handleGenerate}
              disabled={!url.trim()}
              className="btn-primary w-full flex items-center justify-center gap-2 py-3"
            >
              <QrCode size={20} />
              Generar Codigo QR
            </button>
          </div>

          {/* Info Box */}
          <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-600 rounded-lg">
            <h3 className="font-semibold text-blue-900 dark:text-blue-300 mb-2">
              Usos comunes en el ambito judicial
            </h3>
            <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1 list-disc list-inside">
              <li>Traslado de demanda via carta documento</li>
              <li>Enlace a expediente electronico (MEV, LEX100, etc.)</li>
              <li>Link a documentacion en la nube</li>
              <li>Acceso a resoluciones y autos judiciales</li>
              <li>Vinculo a evidencia digital</li>
            </ul>
          </div>
        </div>
      ) : (
        /* Result Section */
        <div className="space-y-6">
          {/* QR Display */}
          <div className="card border-2 border-blue-500">
            <div className="text-center">
              <h2 className="text-xl font-bold text-legal-900 dark:text-white mb-4">
                Tu Codigo QR
              </h2>

              <div ref={qrRef} className="inline-block bg-white p-6 rounded-lg shadow-inner">
                <QRCode
                  value={url}
                  size={256}
                  level="H"
                />
              </div>

              {/* URL Display */}
              <div className="mt-4 flex items-center gap-2 justify-center">
                <code className="text-sm text-legal-600 dark:text-legal-400 bg-legal-100 dark:bg-legal-700 px-3 py-1 rounded max-w-md truncate">
                  {url}
                </code>
                <button
                  onClick={handleCopyUrl}
                  className="btn-secondary p-2"
                  title="Copiar URL"
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Download Options */}
          <div className="card">
            <h3 className="text-lg font-semibold text-legal-900 dark:text-white mb-4">
              Descargar Codigo QR
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => handleDownloadQR('png')}
                className="btn-primary flex items-center justify-center gap-2 py-3"
              >
                <Download size={20} />
                Descargar PNG
              </button>
              <button
                onClick={() => handleDownloadQR('svg')}
                className="btn-secondary flex items-center justify-center gap-2 py-3"
              >
                <Download size={20} />
                Descargar SVG
              </button>
            </div>
            <p className="text-xs text-legal-500 dark:text-legal-400 mt-3 text-center">
              PNG: ideal para documentos. SVG: ideal para impresion de alta calidad.
            </p>
          </div>

          {/* Tips */}
          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-600 rounded-lg">
            <h3 className="font-semibold text-yellow-900 dark:text-yellow-300 mb-2">
              Recomendaciones
            </h3>
            <ul className="text-sm text-yellow-800 dark:text-yellow-200 space-y-1 list-disc list-inside">
              <li>Verifica que la URL sea correcta antes de enviar el documento</li>
              <li>El QR debe tener un tamano mínimo de 2x2 cm para ser legible</li>
              <li>Ubicalo en un lugar visible del documento</li>
              <li>Prueba escanearlo antes de imprimir</li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex gap-4">
            <button onClick={handleReset} className="btn-secondary flex-1">
              Generar Otro QR
            </button>
          </div>
        </div>
      )}

      {/* Privacy Note */}
      <div className="text-center text-sm text-legal-500 dark:text-legal-400">
        <p>
          El QR se genera localmente en tu navegador. No almacenamos ninguna informacion.
        </p>
      </div>
    </div>
  )
}

export default QRGenerator
